import { Controller } from '@hotwired/stimulus'
import { DataTable } from 'simple-datatables'

export default class extends Controller {
  static targets = ['table']
  static values = {
    searchable: Boolean,
    sortable: Boolean,
    fixedHeight: Boolean,
    paging: Boolean,
    perPage: Number,
    perPageSelect: Array,
    filterActive: Boolean,
  }

  connect() {
    this._dtOptions = {
      searchable: this.hasSearchableValue ? this.searchableValue : true,
      sortable: this.hasSortableValue ? this.sortableValue : true,
      fixedHeight: this.hasFixedHeightValue ? this.fixedHeightValue : false,
      paging: this.hasPagingValue ? this.pagingValue : true,
      perPage: this.hasPerPageValue ? this.perPageValue : 100,
      perPageSelect: this.hasPerPageSelectValue
        ? this.perPageSelectValue
        : [[100], [50], [20], [10]],
      // simple-datatables renders a bare <tbody>; put its attributes back so
      // Turbo Streams can still target it by id
      tableRender: (_data, table) => {
        const tbody = table.childNodes.find((node) => node.nodeName === 'TBODY')
        if (tbody) tbody.attributes = { ...tbody.attributes, ...this._tbodyAttributes }
        return table
      },
    }
    const tbody = this.tableTarget.tBodies[0]
    this._tbodyAttributes = Object.fromEntries(
      [...(tbody?.attributes || [])].map(({ name, value }) => [name, value])
    )
    this._build()
  }

  disconnect() {
    if (this.dataTable) {
      this.dataTable.destroy()
    }
  }

  // An empty table renders a "No entries" message that bypasses tableRender
  // and drops the tbody's id, so a stream-targeted table waits for its first
  // row (refreshAfterStream builds it then).
  _build() {
    const tbody = this.tableTarget.tBodies[0]
    if (tbody?.id && !tbody.rows.length) return

    this.dataTable = new DataTable(this.tableTarget, this._dtOptions)
    this._applyDataOrder()
    this.dataTable.on('datatable.search', (query, matched) => {
      this.dispatch('search', { detail: { matched } })
    })
  }

  // simple-datatables sorts by cell text; honour a cell's data-order instead
  // (e.g. a timestamp behind a formatted date)
  _applyDataOrder() {
    this.dataTable.data.data.forEach((row) => {
      row.cells.forEach((cell) => {
        const order = cell.attributes?.['data-order']
        if (order === undefined) return
        cell.order = order !== '' && !isNaN(order) ? Number(order) : order
      })
    })
  }

  refresh() {
    if (this.dataTable) {
      this.dataTable.refresh()
    }
  }

  refreshAfterStream(event) {
    if (this.filterActiveValue) {
      event.preventDefault()
      return
    }

    requestAnimationFrame(() => requestAnimationFrame(() => this._rebuild()))
  }

  _rebuild() {
    if (!this.dataTable) return this._build()

    const base = document.createElement('div')
    base.innerHTML = this.dataTable._initialHTML
    const baseTable = base.querySelector('table')

    const currentTbody = this.dataTable.dom.querySelector('tbody')
    const baseTbody = baseTable.querySelector('tbody')
    if (currentTbody && baseTbody) {
      baseTbody.replaceWith(currentTbody.cloneNode(true))
    }

    // Keep the viewer's sort across the rebuild
    const sort = this.dataTable.columns._state.sort

    this.dataTable._initialHTML = baseTable.outerHTML
    this.dataTable.destroy()
    this.dataTable = null
    this._build()

    if (this.dataTable && sort) this.dataTable.columns.sort(sort.column, sort.dir)
  }
}
